import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Activity, ShieldCheck } from "lucide-react";

import { pageTransition, formatDate } from "@/lib/utils";
import { getAnonymousStatus } from "@/api/complaints";
import { Card, CardHeader, CardTitle } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { SkeletonCard } from "@/components/ui/Skeleton";
import {
  COMPLAINT_CATEGORY_LABELS,
  COMPLAINT_EVENT_LABELS,
  COMPLAINT_SEVERITY_LABELS,
  COMPLAINT_STATUS_LABELS,
  SEVERITY_BADGE_VARIANT,
  STATUS_BADGE_VARIANT,
  type AnonymousStatusView,
} from "@/types/complaint";

export function AnonymousComplaintStatusPage() {
  const { orgSlug, token } = useParams<{ orgSlug: string; token: string }>();

  const [view, setView] = useState<AnonymousStatusView | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orgSlug || !token) {
      setError("Missing tracking link information.");
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getAnonymousStatus(orgSlug, token)
      .then((data) => {
        if (!cancelled) setView(data);
      })
      .catch(() => {
        if (!cancelled)
          setError(
            "We couldn't find that report. Check the link or contact HR if you need help.",
          );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [orgSlug, token]);

  return (
    <div className="min-h-screen bg-background py-12 px-4">
      <div className="max-w-2xl mx-auto">
        <motion.div {...pageTransition}>
          <div className="flex items-center gap-3 mb-6">
            <div className="rounded-xl bg-accent/10 p-2.5">
              <ShieldCheck className="h-6 w-6 text-accent" />
            </div>
            <div>
              <h1 className="text-2xl font-bold font-heading text-text-primary">
                Report status
              </h1>
              <p className="text-sm text-text-muted">
                Anonymous tracking — only the latest visible updates are shown.
              </p>
            </div>
          </div>

          {isLoading && (
            <div className="space-y-4">
              <SkeletonCard />
              <SkeletonCard />
            </div>
          )}

          {!isLoading && error && (
            <Card>
              <p className="text-sm text-text-primary">{error}</p>
            </Card>
          )}

          {!isLoading && !error && view && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>{view.title}</CardTitle>
                </CardHeader>
                <div className="flex flex-wrap gap-2 mb-4">
                  <Badge variant={STATUS_BADGE_VARIANT[view.status]}>
                    {COMPLAINT_STATUS_LABELS[view.status]}
                  </Badge>
                  <Badge variant={SEVERITY_BADGE_VARIANT[view.severity]}>
                    {COMPLAINT_SEVERITY_LABELS[view.severity]}
                  </Badge>
                  <Badge variant="default">
                    {COMPLAINT_CATEGORY_LABELS[view.category]}
                  </Badge>
                </div>

                <dl className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <dt className="text-xs text-text-muted">Submitted</dt>
                    <dd className="text-text-primary">
                      {formatDate(view.created_at)}
                    </dd>
                  </div>
                  {view.triaged_at && (
                    <div>
                      <dt className="text-xs text-text-muted">Triaged</dt>
                      <dd className="text-text-primary">
                        {formatDate(view.triaged_at)}
                      </dd>
                    </div>
                  )}
                  {view.resolved_at && (
                    <div>
                      <dt className="text-xs text-text-muted">Resolved</dt>
                      <dd className="text-text-primary">
                        {formatDate(view.resolved_at)}
                      </dd>
                    </div>
                  )}
                </dl>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle>Activity</CardTitle>
                </CardHeader>
                {view.events.length === 0 ? (
                  <p className="text-sm text-text-muted">
                    No public activity yet. Check back soon.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {view.events.map((e) => (
                      <li key={e.id} className="flex items-start gap-3">
                        <div className="rounded-full bg-accent/10 p-1.5 mt-0.5">
                          <Activity className="h-3.5 w-3.5 text-accent" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-text-primary">
                            {COMPLAINT_EVENT_LABELS[e.event_type] ||
                              e.event_type}
                          </p>
                          <p className="text-xs text-text-muted">
                            {formatDate(e.created_at, "MMM d, yyyy h:mm a")}
                          </p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </Card>

              <p className="text-xs text-text-muted text-center">
                Bookmark this page to check back. The link is the only way to
                view your report — keep it private.
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
